import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { githubApi, type Installation, type Repo } from '@/services/githubApi';
import { docsApi, type TreeNode } from '@/services/docsApi';

// ── IndexedDB helpers ──

const DB_NAME = 'systemdox';
const DB_VERSION = 1;
const STORE_NAME = 'workspace';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ── Context ──

export interface WorkspaceContextValue {
  installations: Installation[];
  selectedOrg: string | null;
  setSelectedOrg: (org: string) => void;

  /** All repos for the selected org */
  repos: Repo[];
  /** Every repo across all installations, unfiltered by the workspace org */
  allRepos: Repo[];
  /** File trees keyed by repo full_name (loaded lazily) */
  trees: Record<string, TreeNode>;
  /** Tree load failures keyed by repo full_name (cleared on retry) */
  treeErrors: Record<string, string>;
  /** Load tree for a specific repo on demand. Cached for the provider's
   *  lifetime; pass `{ force: true }` to re-read a repo whose files may have
   *  changed since (e.g. after committing from the editor). */
  loadTree: (repo: Repo, opts?: { force?: boolean }) => Promise<void>;
  selectedRepo: string | null;
  setSelectedRepo: (repoFullName: string | null) => void;

  loading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

// The last workspace this tab loaded (fast-document-browsing). The Docs pages
// and the book reader each mount their own provider, so every move between
// them refetched the installations and the live GitHub repository listing —
// one to two seconds — before the reader would render. A later mount renders
// from this at once and refreshes it in the background. In memory only: a
// tenant switch and a sign-out both reload the page, so it never outlives the
// identity it was loaded for.
let lastWorkspace: { installs: Installation[]; repos: Repo[] } | null = null;

/** Test seam: forget what this tab loaded. */
export function resetWorkspaceCache(): void {
  lastWorkspace = null;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [allRepos, setAllRepos] = useState<Repo[]>([]);
  const [trees, setTrees] = useState<Record<string, TreeNode>>({});
  const treesRef = useRef(trees);
  treesRef.current = trees;
  const [treeErrors, setTreeErrors] = useState<Record<string, string>>({});
  const [selectedOrg, setSelectedOrgState] = useState<string | null>(null);
  const [selectedRepo, setSelectedRepoState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load installations, repos, and restore saved selections. From this tab's
  // last load first (lastWorkspace), when there is one, then fresh.
  useEffect(() => {
    let cancelled = false;

    function apply(installs: Installation[], repoList: Repo[], savedOrg?: string, savedRepo?: string) {
      setInstallations(installs);
      setAllRepos(repoList);

      if (installs.length > 0) {
        const match = installs.find((i) => i.account_login === savedOrg);
        setSelectedOrgState(match ? match.account_login : installs[0].account_login);
      }

      if (savedRepo && repoList.some((r) => r.full_name === savedRepo)) {
        setSelectedRepoState(savedRepo);
      }
    }

    async function init() {
      // The saved selections are a convenience: a browser without IndexedDB
      // (a private window) still gets its repositories.
      const saved = Promise.all([idbGet<string>('selectedOrg'), idbGet<string>('selectedRepo')])
        .catch(() => [undefined, undefined] as [undefined, undefined]);
      const cached = lastWorkspace;
      if (cached) {
        const [savedOrg, savedRepo] = await saved;
        if (cancelled) return;
        apply(cached.installs, cached.repos, savedOrg, savedRepo);
        setLoading(false);
      }
      try {
        // A failed read keeps what the tab already has rather than emptying it.
        const [installs, repoList, [savedOrg, savedRepo]] = await Promise.all([
          githubApi.listInstallations().catch((e) => { console.warn('Failed to load installations:', e); return null; }),
          githubApi.listRepos().catch((e) => { console.warn('Failed to load repos:', e); return null; }),
          saved,
        ]);
        const fresh = {
          installs: installs ?? cached?.installs ?? [],
          repos: repoList ?? cached?.repos ?? [],
        };
        if (installs && repoList) lastWorkspace = fresh;
        if (!cancelled) apply(fresh.installs, fresh.repos, savedOrg, savedRepo);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, []);

  // Filter repos by selected org (memoized to stabilize useEffect dependencies)
  const repos = useMemo(
    () => selectedOrg
      ? allRepos.filter((r) => r.owner.toLowerCase() === selectedOrg.toLowerCase())
      : allRepos,
    [allRepos, selectedOrg],
  );

  // Clear trees when org/repos change so stale trees don't linger
  useEffect(() => {
    setTrees({});
    setTreeErrors({});
  }, [selectedOrg]);

  // Load tree for a single repo on demand (called when user expands a repo)
  const loadTree = useCallback(async (repo: Repo, opts?: { force?: boolean }) => {
    if (!opts?.force && treesRef.current[repo.full_name]) return;
    setTreeErrors((prev) => {
      if (!(repo.full_name in prev)) return prev;
      const next = { ...prev };
      delete next[repo.full_name];
      return next;
    });
    try {
      const tree = await docsApi.getBranchTree(
        repo.owner, repo.repo, repo.default_branch,
      );
      setTrees((prev) => ({ ...prev, [repo.full_name]: tree }));
    } catch (e) {
      console.warn(`Failed to load tree for ${repo.full_name}:`, e);
      const status = (e as { response?: { status?: number } })?.response?.status;
      const message = status === 404
        ? 'Repository not found — it may not be connected to SystemDox'
        : 'Failed to load file tree';
      setTreeErrors((prev) => ({ ...prev, [repo.full_name]: message }));
    }
  }, []);

  // Clear selected repo when switching orgs
  useEffect(() => {
    if (!selectedOrg) return;
    if (selectedRepo && repos.some((r) => r.full_name === selectedRepo)) return;
    setSelectedRepoState(null);
  }, [selectedOrg, repos, selectedRepo]);

  const setSelectedOrg = useCallback((org: string) => {
    setSelectedOrgState(org);
    idbSet('selectedOrg', org);
    localStorage.setItem('systemdox_selected_org', org);
  }, []);

  const setSelectedRepo = useCallback((repoFullName: string | null) => {
    setSelectedRepoState(repoFullName);
    if (repoFullName) idbSet('selectedRepo', repoFullName);
  }, []);

  return (
    <WorkspaceContext.Provider
      value={{ installations, selectedOrg, setSelectedOrg, repos, allRepos, trees, treeErrors, loadTree, selectedRepo, setSelectedRepo, loading }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
